import { Component, ErrorInfo, Fragment } from 'react';

import { ErrorDialog } from '../ErrorDialog/index.js';

export interface ErrorBoundaryProps {
  minElapsedTimeCatch?: number;
  maxErrorCount?: number;
  children: React.ReactNode;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  moreInfo?: boolean;
  avgTime?: number;
  lastTimestamp?: number;
  errorCount?: number;
  retryKey?: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const hasError = this.state.hasError ?? false;
    const lastTimestamp = this.state.lastTimestamp ?? Date.now();
    const errorCount = this.state.errorCount ?? 0;
    const diffTime = Date.now() - lastTimestamp;
    const avgTime = (diffTime + errorCount * (this.state.avgTime ?? 0)) / (errorCount + 1);
    const minElapsedTimeCatch = this.props.minElapsedTimeCatch ?? 1000;
    const maxErrorCount = this.props.maxErrorCount ?? 2;
    // If the previous error was thrown less than minElapsedTimeCatch ago and counter reached then lift it up
    if (hasError && avgTime < minElapsedTimeCatch && errorCount > maxErrorCount) {
      throw error;
    } else {
      console.warn('Catched error', error, info);
      this.setState({ error, errorCount: errorCount + 1, lastTimestamp: Date.now() });
    }
  }

  handleCloseDialog = () => {
    // Remount the children so recoverable errors (e.g. suspense queries that
    // failed with stale credentials) get retried instead of staying errored.
    // Resetting the runaway-error counter here is intentional: each new close
    // cycle starts from a user click on the dialog, so the reset cannot
    // re-enable an automatic catch/re-render loop.
    this.setState((state) => ({
      hasError: false,
      error: undefined,
      errorCount: 0,
      avgTime: undefined,
      lastTimestamp: undefined,
      retryKey: (state.retryKey ?? 0) + 1,
    }));
  };

  render() {
    return (
      <Fragment key={this.state.retryKey ?? 0}>
        {this.props.children}
        {this.state.hasError && this.state.error && (
          <ErrorDialog
            error={this.state.error}
            maxWidth="md"
            message="Don't be panic the current state of your work shouldn't be affected."
            open={true}
            onClose={this.handleCloseDialog}
          />
        )}
      </Fragment>
    );
  }
}
